package com.evoapps.evoshift.widgets

import androidx.compose.runtime.Composable
import androidx.compose.runtime.remember
import androidx.compose.runtime.staticCompositionLocalOf
import androidx.compose.ui.hapticfeedback.HapticFeedback
import androidx.compose.ui.hapticfeedback.HapticFeedbackType
import androidx.compose.ui.platform.LocalHapticFeedback

/**
 * SH-158: what the hand feels when an action lands. Screens call it at the moment the result is
 * known: [confirm] when something was done (clocked in, approved, deleted), [reject] when it was
 * refused, [tick] for a small step (a choice made, a request declined). Phones and watches buzz;
 * desktop and the web have nothing to buzz (the platform ignores it).
 */
interface ActionFeedback {
    fun confirm()
    fun reject()
    fun tick()
}

/** Nothing (previews, screen-only tests); the shell provides the device's haptics. */
object NoActionFeedback : ActionFeedback {
    override fun confirm() {}
    override fun reject() {}
    override fun tick() {}
}

/** The device's haptics through Compose's [HapticFeedback]. */
class HapticActionFeedback(private val haptics: HapticFeedback) : ActionFeedback {
    override fun confirm() = haptics.performHapticFeedback(HapticFeedbackType.Confirm)
    override fun reject() = haptics.performHapticFeedback(HapticFeedbackType.Reject)
    override fun tick() = haptics.performHapticFeedback(HapticFeedbackType.SegmentTick)
}

/** The screens' [ActionFeedback]. */
val LocalActionFeedback = staticCompositionLocalOf<ActionFeedback> { NoActionFeedback }

/**
 * The feedback the shell provides: one given from outside (a test's recorder) is kept, otherwise
 * the device's haptics.
 */
@Composable
fun rememberActionFeedback(): ActionFeedback {
    val given = LocalActionFeedback.current
    if (given !== NoActionFeedback) return given
    val haptics = LocalHapticFeedback.current
    return remember(haptics) { HapticActionFeedback(haptics) }
}
